import { useCallback, useMemo, useState } from 'react';

import { useCoverageAvailability } from '@apps/web/features/coverage/api/coverage_queries';
import {
    type CoverageRow,
    type DayPartKey,
    EMPTY_FILTERS,
    type WorkWeekStartDay,
} from '@apps/web/features/coverage/model/coverage_types';
import {
    type CoverageKpis,
    type CoverageSearchState,
    type CoverageSearchUpdate,
    type CoverageView,
    deriveCoverageDashboardData,
} from '@apps/web/features/coverage/model/dashboard_model';
import { type RequestStatus } from '@apps/web/features/coverage/model/request_model';
import {
    columnDayIndexes,
    type CoverageArea,
    type CoverageBucket,
} from '@apps/web/features/coverage/model/rollup_model';
import { useCoverageAnalytics } from '@apps/web/features/coverage/model/use_coverage_analytics';
import { useCoverageExports } from '@apps/web/features/coverage/model/use_coverage_exports';
import {
    type CoverageClientDensityStatus,
    type CoverageMapStatus,
    useCoverageMap,
} from '@apps/web/features/coverage/model/use_coverage_map';
import { shiftWeek } from '@apps/web/features/coverage/model/week_model';
import {
    type WorkforceMapClientDensityCell,
    type WorkforceMapPoint,
} from '@libraries/typescript/api_client';

export type {
    CoverageSearchState,
    CoverageSearchUpdate,
    CoverageView,
} from '@apps/web/features/coverage/model/dashboard_model';
export { type CoverageMapStatus } from '@apps/web/features/coverage/model/use_coverage_map';

export type CoverageDashboard = {
    readonly isLoading: boolean;
    readonly hasError: boolean;
    readonly refreshErrorMessage: string | null;
    readonly retry: () => void;
    readonly weekStart: string | undefined;
    readonly weekEnd: string | undefined;
    readonly workWeekStartDay: WorkWeekStartDay;
    readonly rows: Array<CoverageRow>;
    readonly facetOptions: ReturnType<
        typeof deriveCoverageDashboardData
    >['facetOptions'];
    readonly kpis: CoverageKpis;
    readonly coverage: Array<CoverageBucket>;
    readonly mapStatus: CoverageMapStatus;
    readonly mapErrorMessage: string | null;
    readonly mapRefreshErrorMessage: string | null;
    readonly mapPoints: ReadonlyArray<WorkforceMapPoint>;
    readonly mapClientDensityCells: ReadonlyArray<WorkforceMapClientDensityCell>;
    readonly mapClientDensityStatus: CoverageClientDensityStatus;
    readonly mapClientDensityErrorMessage: string | null;
    readonly visibleCaregiverIds: ReadonlySet<string>;
    readonly mapCoverageBanner: string;
    readonly retryMap: () => void;
    readonly setPlotClients: (plotClients: boolean) => void;
    readonly canRequestGeocode: boolean;
    readonly geocodeStatus: RequestStatus;
    readonly geocodeError: string | null;
    readonly requestGeocode: () => Promise<void>;
    readonly dismissGeocodeError: () => void;
    readonly hasAnyCaregivers: boolean;
    readonly hasAnyAvailabilityData: boolean;
    readonly filtersActive: boolean;
    // Changes whenever the organization or any filter/week input that
    // redefines the row set changes (excludes view/area, which don't).
    // Consumers key a remountable subtree on this to reset row-scoped local
    // state (e.g. bulk selection) instead of hand-rolling a reset effect.
    readonly rowsResetKey: string;
    readonly goToWeek: (offsetWeeks: number) => void;
    readonly goToCurrentWeek: () => void;
    readonly setFilters: (update: CoverageSearchUpdate) => void;
    readonly clearFilters: () => void;
    readonly clearEpoch: number;
    readonly setView: (view: CoverageView) => void;
    readonly setArea: (area: CoverageArea) => void;
    readonly toggleUnknownOnly: () => void;
    readonly selectCoverageCell: (
        areaKey: string,
        group: 'weekday' | 'weekend',
        dayPart: DayPartKey,
    ) => void;
    readonly setIncludeInactive: (includeInactive: boolean) => void;
    readonly exportStatus: RequestStatus;
    readonly exportError: string | null;
    readonly dismissExportError: () => void;
    readonly exportCsv: () => Promise<void>;
    readonly exportXlsx: () => Promise<void>;
};

export function useCoverageDashboard({
    organizationId,
    search,
    onSearchChange,
    isAdmin,
}: {
    readonly organizationId: string | null;
    readonly search: CoverageSearchState;
    readonly onSearchChange: (update: CoverageSearchUpdate) => void;
    readonly isAdmin: boolean;
}): CoverageDashboard {
    const {
        data,
        error,
        hasRequestFailed,
        isLoading,
        mutate: mutateAvailability,
    } = useCoverageAvailability({
        organizationId,
        weekStart: search.week,
        includeInactive: search.includeInactive,
    });
    const derived = useMemo(
        () => deriveCoverageDashboardData(data, search),
        [data, search],
    );

    useCoverageAnalytics({
        organizationId,
        isReady: !isLoading,
        currentView: search.view,
    });

    const coverageMap = useCoverageMap({
        organizationId,
        enabled: search.view === 'map',
        includeInactive: search.includeInactive,
        plotClients: search.plotClients,
        rows: derived.rows,
        visibleCaregiverIds: derived.visibleCaregiverIds,
        isAdmin,
    });

    const navigationWeekStart = search.week ?? data?.week_start;
    const goToWeek = useCallback(
        (offsetWeeks: number) => {
            if (navigationWeekStart) {
                onSearchChange({
                    week: shiftWeek(navigationWeekStart, offsetWeeks),
                });
            }
        },
        [navigationWeekStart, onSearchChange],
    );
    const goToCurrentWeek = useCallback(() => {
        onSearchChange({ week: undefined });
    }, [onSearchChange]);
    const setFilters = useCallback(
        (update: CoverageSearchUpdate) => {
            const touchesCellSelection =
                update.days !== undefined || update.dayParts !== undefined;
            onSearchChange(
                touchesCellSelection
                    ? { ...update, matchMode: 'every' }
                    : update,
            );
        },
        [onSearchChange],
    );
    const [clearEpoch, setClearEpoch] = useState(0);
    const clearFilters = useCallback(() => {
        setClearEpoch((epoch) => epoch + 1);
        onSearchChange({ ...EMPTY_FILTERS });
    }, [onSearchChange]);
    const setView = useCallback(
        (view: CoverageView) => onSearchChange({ view }),
        [onSearchChange],
    );
    const setArea = useCallback(
        (area: CoverageArea) => onSearchChange({ area }),
        [onSearchChange],
    );
    const setIncludeInactive = useCallback(
        (includeInactive: boolean) => onSearchChange({ includeInactive }),
        [onSearchChange],
    );
    const setPlotClients = useCallback(
        (plotClients: boolean) => onSearchChange({ plotClients }),
        [onSearchChange],
    );
    const toggleUnknownOnly = useCallback(() => {
        onSearchChange({
            days: [],
            dayParts: [],
            matchMode: 'every',
            availability:
                search.availability === 'unknown_only' ? 'any' : 'unknown_only',
        });
    }, [onSearchChange, search.availability]);
    const selectCoverageCell = useCallback(
        (
            areaKey: string,
            group: 'weekday' | 'weekend',
            dayPart: DayPartKey,
        ) => {
            const areaFilter: CoverageSearchUpdate =
                search.area === 'office'
                    ? { offices: [areaKey] }
                    : search.area === 'city'
                      ? { cities: [areaKey] }
                      : { zips: [areaKey] };
            onSearchChange({
                ...areaFilter,
                days: columnDayIndexes(group, derived.workWeekStartDay),
                dayParts: [dayPart],
                matchMode: 'any',
                view: 'grid',
            });
        },
        [derived.workWeekStartDay, onSearchChange, search.area],
    );

    const exports = useCoverageExports({
        data,
        rows: derived.rows,
        workWeekStartDay: derived.workWeekStartDay,
    });

    // Row-set-affecting inputs only: view/area/plotClients change which UI
    // is shown, not which caregivers are in `rows`, so they're excluded.
    const {
        view: _view,
        area: _area,
        plotClients: _plotClients,
        ...rowRelevantSearch
    } = search;
    const rowsResetKey = `${organizationId ?? 'none'}:${JSON.stringify(rowRelevantSearch)}`;

    return {
        isLoading,
        hasError: hasRequestFailed && data === undefined,
        refreshErrorMessage:
            error !== undefined && data !== undefined
                ? 'We couldn’t refresh this view. Showing previously loaded data.'
                : null,
        retry: () => {
            void mutateAvailability();
        },
        weekStart: data?.week_start ?? search.week,
        weekEnd:
            data?.week_end ??
            (search.week ? shiftWeek(search.week, 1) : undefined),
        workWeekStartDay: derived.workWeekStartDay,
        rows: derived.rows,
        facetOptions: derived.facetOptions,
        kpis: derived.kpis,
        coverage: derived.coverage,
        mapStatus: coverageMap.status,
        mapErrorMessage: coverageMap.errorMessage,
        mapRefreshErrorMessage: coverageMap.refreshErrorMessage,
        mapPoints: coverageMap.points,
        mapClientDensityCells: coverageMap.clientDensityCells,
        mapClientDensityStatus: coverageMap.clientDensityStatus,
        mapClientDensityErrorMessage: coverageMap.clientDensityErrorMessage,
        visibleCaregiverIds: derived.visibleCaregiverIds,
        mapCoverageBanner: coverageMap.coverageBanner,
        retryMap: coverageMap.retry,
        setPlotClients,
        canRequestGeocode: coverageMap.canRequestGeocode,
        geocodeStatus: coverageMap.geocodeStatus,
        geocodeError: coverageMap.geocodeError,
        requestGeocode: coverageMap.requestGeocode,
        dismissGeocodeError: coverageMap.dismissGeocodeError,
        hasAnyCaregivers: derived.hasAnyCaregivers,
        hasAnyAvailabilityData: derived.hasAnyAvailabilityData,
        filtersActive: derived.filtersActive,
        rowsResetKey,
        goToWeek,
        goToCurrentWeek,
        setFilters,
        clearFilters,
        clearEpoch,
        setView,
        setArea,
        toggleUnknownOnly,
        selectCoverageCell,
        setIncludeInactive,
        exportStatus: exports.status,
        exportError: exports.error,
        dismissExportError: exports.dismissError,
        exportCsv: exports.exportCsv,
        exportXlsx: exports.exportXlsx,
    };
}
